// @ts-check
"use strict"

import React from "react";
import { ChatMarkdown } from "../chat-bubble/ChatBubble";
import { parseJsonToMarkdown } from "./utils";
import "./report-view.css";

/**
 * JSX-based report view component for display purposes
 * Now uses markdown rendering for the main report content
 * 
 * @param {Object} props
 * @param {string} [props.blobImageUrl]
 * @param {import(".").ReportData} props.reportData
 * @param {string} [props.rawReportJson] - Raw JSON string from report.current
 */
export default function ReportView({ blobImageUrl, reportData, rawReportJson }) {
  if (!reportData) return null;
  
  let age = reportData?.patient?.age ?? reportData?.patientInfo?.age ?? 0;
  let formattedAge = `${age} ${age === 1 ? "year" : "years"}`;
  
  // Use raw JSON if provided, otherwise fallback to existing content
  const markdownContent = rawReportJson ? parseJsonToMarkdown(rawReportJson) : null;
  
  return (
    <div className="report-view">
      <div className="report-view__page">
        <header className="report-view__header">
          <h1 className="report-view__title">
            {reportData?.facility?.name ?? "NAME OF HOSPITAL"}
          </h1>
        </header>

        {/* Details container with two-column layout for info and image */}
        <div className="report-view__details-container">
          {/* Two-column container for patient and facility info */}
          <div className="report-view__info-columns-container">
            {/* Left column: Patient details */}
            <div className="report-view__info-column">
              <div className="report-view__row">
                <span className="report-view__label">Patient Name:</span>
                <span className="report-view__value">{reportData?.patient?.name ?? reportData?.patientInfo?.name ?? ""}</span>
              </div>
              <div className="report-view__row">
                <span className="report-view__label">Age:</span>
                <span className="report-view__value">{formattedAge}</span>
              </div>
              <div className="report-view__row">
                <span className="report-view__label">Patient ID:</span>
                <span className="report-view__value">{reportData?.patient?.id ?? reportData?.patientInfo?.id ?? ""}</span>
              </div>
              <div className="report-view__row">
                <span className="report-view__label">Gender:</span>
                <span className="report-view__value">{reportData?.patient?.gender ?? reportData?.patientInfo?.gender ?? ""}</span>
              </div>
            </div>
            
            {/* Right column: Facility details */}
            <div className="report-view__info-column">
              <div className="report-view__row">
                <span className="report-view__label">Facility Name:</span>
                <span className="report-view__value">{reportData?.facility?.name ?? ""}</span>
              </div>
              <div className="report-view__row">
                <span className="report-view__label">Reporting Doctor:</span>
                <span className="report-view__value">{reportData?.facility?.reportingDoctor ?? ""}</span>
              </div>
              <div className="report-view__row">
                <span className="report-view__label">Report Date:</span>
                <span className="report-view__value">{reportData?.date?.split("T")[0] ?? ""}</span>
              </div>
            </div>
          </div>

          {/* Right side: Patient image */}
          {blobImageUrl && (
            <div className="report-view__image-container">
              <img
                src={blobImageUrl}
                alt="Patient"
                className="report-view__patient-image"
              />
            </div>
          )}
        </div>

        {/* Additional metadata sections */}
        {reportData?.modality && (
          <div className="report-view__section">
            <h3 className="report-view__section-title">Modality</h3>
            <ReportFieldValue value={reportData?.modality} />
          </div>
        )}

        {reportData?.technique && (
          <div className="report-view__section">
            <h3 className="report-view__section-title">Technique</h3>
            <ReportFieldValue value={reportData?.technique} />
          </div>
        )}

        {reportData?.examinationReason && (
          <div className="report-view__section">
            <h3 className="report-view__section-title">Reason for examination</h3>
            <ReportFieldValue value={reportData?.examinationReason} />
          </div>
        )}

        {reportData?.clinicalHistory && (
          <div className="report-view__section">
            <h3 className="report-view__section-title">Relevant clinical history</h3>
            <ReportFieldValue value={reportData?.clinicalHistory} />
          </div>
        )}

        {/* Main report content - use markdown if available, otherwise fallback to field rendering */}
        {markdownContent ? (
          <div className="report-view__markdown-content">
            <ChatMarkdown>{markdownContent}</ChatMarkdown>
          </div>
        ) : (
          reportData?.content?.map((field) => (
            <div className="report-view__section" key={field.label}>
              <h3 className="report-view__section-title">{field.label}</h3>
              <ReportFieldValue value={field.value} />
            </div>
          ))
        )}

        <footer className="report-view__footer">
          <div className="report-view__footer-content">
            <span className="report-view__footer-text">Powered by Moremi AI</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Component to render report field values with proper formatting
 * 
 * @param {Object} props
 * @param {string} [props.valueKey]
 * @param {import(".").ReportField["value"]} props.value
 */
function ReportFieldValue({ valueKey, value }) {
  const transformedValue = (() => {
    if (value == null) return "";
    if (Array.isArray(value) && value.length === 0) return "";
    if (Array.isArray(value) && value.length === 1) return value[0];
    return value;
  })();

  if (typeof transformedValue === "string") {
    return (
      <div className="report-view__field">
        {valueKey != null && (
          <span className="report-view__field-key">{valueKey}: </span>
        )}
        <span className="report-view__field-value">{transformedValue ?? ""}</span>
      </div>
    );
  }

  if (Array.isArray(transformedValue)) {
    return (
      <div className="report-view__field">
        {valueKey != null && (
          <span className="report-view__field-key">{valueKey}: </span>
        )}
        <ul className="report-view__list">
          {transformedValue?.map((item, index) => (
            <li key={index} className="report-view__list-item">
              {item ?? ""}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (typeof transformedValue === "object") {
    return (
      <div className="report-view__field">
        {valueKey != null && (
          <span className="report-view__field-key">{valueKey}: </span>
        )}
        <ul className="report-view__list">
          {Object.entries(transformedValue)?.map(([entryKey, entryValue]) => (
            <li key={entryKey} className="report-view__list-item">
              <ReportFieldValue valueKey={entryKey} value={entryValue} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
}